export function attachHeroNavigation(root: HTMLElement, navigate: (hash: string) => void) {
  const onClick = (event: MouseEvent) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = (event.target as Element).closest<HTMLAnchorElement>('#mr-main-one .mrh__actions a[href^="#"]');
    if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
    const hash = anchor.getAttribute('href')!;
    let target: HTMLElement | null;
    try { target = document.getElementById(decodeURIComponent(hash.slice(1))); } catch { return; }
    if (!target || !root.contains(target)) return;
    event.preventDefault();
    navigate(hash);
    // Move keyboard focus without jumping; scroll-margin clears the fixed header.
    target.focus({ preventScroll: true });
    target.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };
  root.addEventListener('click', onClick);
  return () => root.removeEventListener('click', onClick);
}

export function attachHeroCounters(root: HTMLElement) {
  const stats = root.querySelector<HTMLElement>('#mr-main-one .mrh-stats');
  const counters = Array.from(stats?.querySelectorAll<HTMLElement>('[data-hero-count]') || []);
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (!stats || !counters.length || motion.matches || !('IntersectionObserver' in window)) return () => {};
  let elapsed = 0;
  let previous: number | undefined;
  let frame = 0;
  let visible = false;
  let finished = false;
  const duration = 1450;
  const stagger = 80;
  const stop = () => { window.cancelAnimationFrame(frame); frame = 0; previous = undefined; };
  const finish = () => {
    finished = true;
    stop();
    counters.forEach(counter => { counter.textContent = counter.dataset.heroCount!; });
    observer.disconnect();
  };
  const step = (now: number) => {
    frame = 0;
    if (previous !== undefined) elapsed += now - previous;
    previous = now;
    counters.forEach((counter, index) => {
      const progress = Math.min(1, Math.max(0, (elapsed - index * stagger) / duration));
      const value = String(Math.round(Number(counter.dataset.heroCount) * (1 - (1 - progress) ** 3)));
      if (counter.textContent !== value) counter.textContent = value;
    });
    if (elapsed >= duration + (counters.length - 1) * stagger) finish();
    else frame = window.requestAnimationFrame(step);
  };
  const sync = () => {
    if (motion.matches) { finish(); return; }
    if (!visible || document.hidden || finished) { stop(); return; }
    if (!frame) frame = window.requestAnimationFrame(step);
  };
  const observer = new IntersectionObserver(entries => {
    visible = entries[0].isIntersecting;
    sync();
  }, { threshold: 0.1 });
  // Static HTML and the screen-reader copy always contain the complete figures.
  counters.forEach(counter => { counter.textContent = '0'; });
  observer.observe(stats);
  document.addEventListener('visibilitychange', sync);
  motion.addEventListener('change', sync);
  return () => {
    finish();
    document.removeEventListener('visibilitychange', sync);
    motion.removeEventListener('change', sync);
  };
}
